import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiV1 } from "api-v2";
import { useCurrentCompanyId } from "hooks/auth";
import { showToast } from "utils/helpers";
import type { UserListItem } from "../../../types/userInvite";

export interface AdminUpdateUserPayload {
  first_name: string;
  last_name: string;
  phone: string | null;
}

interface AdminUpdateUserArgs {
  payload: AdminUpdateUserPayload;
  userId: number;
}

interface AdminUpdateUserError {
  code?: string;
  message: string;
}

const adminUpdateUser = async (userId: number, payload: AdminUpdateUserPayload): Promise<UserListItem> => {
  const response = await apiV1.patch<{ data: UserListItem }>(`users/${userId}/profile`, payload);
  return response.data.data;
};

export const useAdminUpdateUserMutation = () => {
  const queryClient = useQueryClient();
  const companyId = useCurrentCompanyId();

  return useMutation<UserListItem, AdminUpdateUserError, AdminUpdateUserArgs>({
    mutationFn: ({ userId, payload }) => adminUpdateUser(userId, payload),
    retry: false,
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      queryClient.invalidateQueries({ queryKey: ["users", companyId] });
      showToast("User updated successfully", "Success", true);
    },
    onError: (error) => {
      showToast(
        error?.message || "Failed to update user. Please try again.",
        "Error",
      );
    },
  });
};
